import Page from '../../../enums/Page';
import Status from '../../../enums/Status';
import {exitExpressCheckoutFlow} from '../../actions/expressCheckoutActions';
import {showOverlay} from '../../actions/overlay-actions';
import {setPageMessage} from '../../actions/pageMessageActions';
import checkoutWithAmazonpay from '../../actions/paymentActions/amazonpay/checkoutWithAmazonpay';
import checkoutWithNewpay from '../../actions/paymentActions/newpay/checkoutWithNewpay';
import {
  setPaymentOptionErrorMessage,
  setTranslatedPaymentOptionErrorMessage,
} from '../../actions/paymentActions/payment-actions';
import checkoutWithRazorpay from '../../actions/paymentActions/razorpay/checkoutWithRazorpay';
import setDeviceDataCollectionComplete from '../../actions/paymentActions/setDeviceDataCollectionComplete';
import {setWechatProcessed, startOrderProcessing, stopOrderProcessing} from '../../actions/site-actions';
import {TooltipAndOverlayIDs} from '../../components/detailed-tooltip/TooltipLibrary';
import language from '../../lang/language';
import Translation from '../../lang/Translation';
import getPage from '../../page/getPage';
import {beginApplePaySession, completeApplePayPayment} from '../../payment-methods/applepay/applepay-api';
import getIsUsingExpressCheckout from '../../selectors/ExpressSelectors/getIsUsingExpressCheckout';
import {
  getCurrency, getLocaleFromSiteDataV2, getSelectedPaymentOptionType, getSettings, getSiteId, isOrderProcessing,
} from '../../selectors/selectors';
import getShouldCollectThreeDSData from '../../selectors/ThreeDSSelectors/getShouldCollectThreeDSData';
import store from '../../store/store';
import doMetric from '../../tracking/bigQueryMetricRecorder';
import sendOrderMetric from '../../tracking/bigQueryOrderRecorder';
import forwardToPaymentURL from '../../utils/forwardToPaymentURL';
import forwardToURL from '../../utils/forwardToURL';
import redirectToAfterPay from '../../utils/redirectToAfterpay';
import Track from '../../utils/tracking';
import * as pollers from '../pollers/pollers';
import {placeOrderViaApiPoller} from '../pollers/pollers';
import {handleThreeDSecureRequired} from './statusHandlers/handleThreeDSecureRequired';
import isCAAS from '../../utils/isCAAS';

function pageDependent(checkout, change, resolve) {
  switch (getPage()) {
    case Page.PAYMENT_CHANGE:
      return change;
    case Page.PAYMENT_RESOLUTION:
      return resolve;
    default:
      return checkout;
  }
}

const getGenericErrorKey = () => pageDependent(
  'errors.placeOrderFailError',
  'errors.updatePaymentDetailError',
  'errors.updatePaymentDetailError',
);

const stopWithError = (key = getGenericErrorKey()) => {
  store.dispatch(setPageMessage(Translation.getText(key)));
  store.dispatch(stopOrderProcessing());
};

const setPaymentError = (paymentError, pageError, isPreTranslated = false) => {
  if (getIsUsingExpressCheckout(store.getState())) {
    store.dispatch(exitExpressCheckoutFlow(`${paymentError}`, true));
  }
  
  const selectedOptionIndex = store.getState().payment.paymentOptionIndex;
  if (isPreTranslated) {
    store.dispatch(setTranslatedPaymentOptionErrorMessage(selectedOptionIndex, paymentError));
  } else {
    store.dispatch(setPaymentOptionErrorMessage(selectedOptionIndex, paymentError));
  }
  stopWithError(pageError);
};

const snakeToCamelcase = (status) => {
  if (status) {
    return status.toLowerCase()
      .replace(/(_[a-z])/g, $1 => $1.toUpperCase().replace('_', ''));
  }
  return null;
};

export default function orderStatusPollerSuccess(response) {
  const {body: orderJSON} = response;
  const error = orderJSON.errorStatus;
  const status = orderJSON.orderStatus;
  const {errorMessages} = orderJSON;
  const state = store.getState();
  const settings = getSettings(state);
  const siteId = getSiteId(state);
  
  if (settings?.enable_bq_logging_for_order_status) {
    try {
      doMetric('orderStatus', [{name: 'orderStatusPollerSuccessOrderStatus', value: status}]);
    } catch (errorMsg) {
      Track.track(errorMsg);
    }
  }
  
  switch (status) {
    case 'SUCCESS': {
      if (isCAAS(siteId)) {
        try {
          sendOrderMetric();
        } catch (metricError) {
          Track.track(`Error sending Caas order event to BQ from OrderStatusPollerSuccess.js ${metricError}`);
        }
      }
      const orderCompleteURL = orderJSON.orderCompleteRedirectUrl
        || pageDependent('/checkout/complete', '/checkout/payment-changed', '/checkout/payment-resolved');
      forwardToURL(orderCompleteURL);
      break;
    }
    case 'MODE_PAYMENT_ATTRIBUTES_RECEIVED':
      forwardToURL('/checkout/processing');
      break;
    case 'PAYPAL_TOKEN_RECEIVED':
    case 'OPTTY_TOKEN_RECEIVED':
    case 'ADYEN_TOKEN_RECEIVED':
    case 'FLEX_TOKEN_RECEIVED':
    case 'ADYEN_CANCELLED':
    case 'WECHAT_AUTH_REQUESTED':
      forwardToPaymentURL(orderJSON.paymentAccessUrl);
      break;
    case 'HOSTED_PAYMENT_TOKEN_RECEIVED':
      if (orderJSON.paymentAccessUrl !== null) {
        forwardToPaymentURL(orderJSON.paymentAccessUrl);
      } else if (orderJSON.redirectToken !== null) {
        redirectToAfterPay(orderJSON.paymentMethod, orderJSON.redirectToken)
          .catch(() => {
            stopWithError();
          });
      }
      break;
    case 'FRASERS_PLUS_PAYMENT_ATTRIBUTES_RECEIVED':
      if (orderJSON?.paymentMethodAttributes?.redirectUrl) {
        forwardToPaymentURL(orderJSON.paymentMethodAttributes.redirectUrl);
      } else {
        stopWithError();
        Track.error('Unable to checkout with FraserPlus');
      }
      break;
    case 'AMAZONPAY_PAYMENT_ATTRIBUTES_RECEIVED':
      // eslint-disable-next-line no-case-declarations
      const state = store.getState();
      // eslint-disable-next-line no-case-declarations
      const settings = getSettings(state);
      // eslint-disable-next-line no-case-declarations
      const currency = getCurrency(state);
      // eslint-disable-next-line no-case-declarations
      const ledgerCurrencyFromSetting = settings?.amazonpay_ledger_currency_map?.[currency];
      try {
        checkoutWithAmazonpay({
          ...orderJSON?.paymentMethodAttributes,
          amazonpayLedgerCurrency: ledgerCurrencyFromSetting || currency,
          amazonpaySetPublicKeyOnTopLevel: settings?.amazonpay_set_public_key_on_top_level,
          amazonpayEnvironmentIsSandbox: settings?.amazonpay_environment_is_sandbox,
          amazonpayCheckoutLanguage: settings?.amazonpay_checkout_language || getLocaleFromSiteDataV2(state),
        });
      } catch (e) {
        setPaymentError('amazonpay.checkout.error');
        Track.error(`Unable to checkout with Amazonpay ${e}`);
      }
      break;
      
    case 'RAZORPAY_PAYMENT_ATTRIBUTES_RECEIVED':
      try {
        checkoutWithRazorpay({
          ...orderJSON?.paymentMethodAttributes,
        });
      } catch (e) {
        setPaymentError('razorpay.checkout.error');
        Track.error(`Unable to checkout with Razorpay ${e}`);
      }
      break;
  
    case 'WECHAT_USER_WAITING': {
      const wechatOverlayIsActive = store.getState().overlay?.selectedOverlay === 'wechat';
      if (!wechatOverlayIsActive) {
        store.dispatch(showOverlay('wechat', orderJSON.paymentAccessUrl));
        setTimeout(() => {
          placeOrderViaApiPoller.runPoller();
        }, 5000);
      } else {
        setTimeout(() => {
          placeOrderViaApiPoller.runPoller();
        }, 3500);
      }
      break;
    }
    case 'WECHAT_USER_PAID':
      if (!store.getState().site.isWechatProcessed) {
        store.dispatch(setWechatProcessed(true));
        store.dispatch(startOrderProcessing());
      }
      setTimeout(() => {
        placeOrderViaApiPoller.runPoller();
      }, 1500);
      break;
    case 'KLARNA_AUTH_REQUESTED':
    case 'KLARNA_PLACE_ORDER_REQUESTED': {
      const klarnaStatus = store.getState().klarna.status;
      if (klarnaStatus === Status.PLACING_ORDER) {
        setTimeout(() => {
          placeOrderViaApiPoller.runPoller();
        }, 2000);
      } else if (klarnaStatus !== Status.ERROR) {
        store.dispatch(showOverlay(TooltipAndOverlayIDs.KLARNA));
      }
      break;
    }
    case 'KLARNA_PLACE_ORDER_RESPONSE_RETURNED':
      if (isOrderProcessing(store.getState())) {
        forwardToPaymentURL(orderJSON.paymentAccessUrl);
      } else {
        store.dispatch(startOrderProcessing());
      }
      break;
    case 'CARD_AUTHENTICATION_FAILED':
      if (getSettings(store.getState()).threeds_card_auth_device_update && getShouldCollectThreeDSData(store.getState())) {
        store.dispatch(setDeviceDataCollectionComplete());
      }

      setPaymentError('errors.cardAuthenticationFailed');
      break;
    case 'HOSTED_PAYMENT_AUTHENTICATION_FAILED':
      setPaymentError('errors.hostedPaymentAuthenticationFailed');
      break;
    case 'PAYPAL_AUTHENTICATION_FAILED':
    case 'PAYPAL_CURRENTLY_UNAVAILABLE':
    case 'RESERVE_AMOUNT_AUTH_FAILED':
    case 'RESERVE_AMOUNT_ERROR':
    case 'RESERVE_AMOUNT_TIMEOUT': {
      const propName = snakeToCamelcase(status);
      setPaymentError(`errors.${propName}`);
      break;
    }
    case 'HOSTED_PAYMENT_CURRENTLY_UNAVAILABLE': {
      const convertedStatus = snakeToCamelcase(status);
      const convertedError = snakeToCamelcase(error);
      const paymentMethod = getSelectedPaymentOptionType(store.getState());
      if (paymentMethod && paymentMethod.toUpperCase() === 'PAYVYNE') {
        if (language.doesTranslationExist(`paymentOptions.errors.${convertedError}`)) {
          setPaymentError(`paymentOptions.errors.${convertedError}`, `paymentOptions.errors.${convertedError}`);
        } else {
          setPaymentError(`errors.${convertedStatus}`);
        }
      } else {
        setPaymentError(`errors.${convertedStatus}`);
      }
      break;
    }
    case 'ADYEN_AUTHENTICATION_FAILED':
    case 'ADYEN_CURRENTLY_UNAVAILABLE':
      setPaymentError('errors.adyenAuthenticationFailed', 'errors.adyenAuthenticationFailed');
      break;
    case 'GOOGLEPAY_FAILURE':
      setPaymentError('errors.hostedPaymentCurrentlyUnavailable');
      break;
    case 'ADYEN_V2_ACTIONS_RECEIVED':
      if (orderJSON.actionsMap) {
        store.dispatch(showOverlay(TooltipAndOverlayIDs.ADYEN_V2_ACTION, orderJSON.actionsMap));
      } else {
        forwardToURL('/checkout/processing');
      }
      break;
    case 'READY_TO_SET_ORDER_PAYMENT':
    case 'READY_TO_PLACE_ORDER':
    case 'SET_ORDER_PAYMENT_REQUESTED':
      setTimeout(() => {
        placeOrderViaApiPoller.runPoller();
      }, 3500);
      break;
    case 'APPLE_PAY_MERCHANT_VALIDATION_REQUIRED':
    case 'APPLE_PAY_MERCHANT_VALIDATION_REQUESTED':
    case 'APPLE_PAY_CREATE_ORDER_REQUIRED':
    case 'APPLE_PAY_CREATE_ORDER_REQUESTED':
      if (!store.getState().site.isApplePaySessionStarted && isOrderProcessing(store.getState())) {
        beginApplePaySession();
      }
      break;
    case 'APPLE_PAY_CREATE_ORDER_COMPLETE':
      completeApplePayPayment();
  
      setTimeout(() => {
        placeOrderViaApiPoller.runPoller();
      }, 3500);
      break;
    case 'THREE_DS_CONFIRMATION_REQUIRED':
      const threeDsStatus = handleThreeDSecureRequired(orderJSON);
      
      if (threeDsStatus === 'START_BACKGROUND_POLL') {
        setTimeout(() => {
          if (isOrderProcessing(store.getState())) {
            pollers.placeOrderViaApiPoller.runPoller();
          }
        }, 1000);
      }
      break;
    case 'PROVIDER_VALIDATION_FAILURE':
      if (errorMessages?.length > 0) {
        setPaymentError(errorMessages[0], 'errors.mainFormError', true);
      } else {
        stopWithError();
      }
      break;
  
    case 'FRAUD_REJECTION_PREPAID_CARD':
      setPaymentError('errors.fraud.prepaid.card.reject', 'errors.fraud.prepaid.card.reject');
      break;
    case 'FRAUD_REJECTION_UNKNOWN':
      setPaymentError('errors.cardAuthenticationFailed', 'errors.cardAuthenticationFailed');
      break;
    case 'NEWPAY_USER_WAITING':
      if (orderJSON.checkoutCertificate) {
        store.dispatch(checkoutWithNewpay(orderJSON.checkoutCertificate, stopWithError));
      } else {
        stopWithError();
      }
      break;
    default: // primarily handles 'FAILURE' but also any unknown statuses.
      // This ensures the ThreeDSIdentify component does not return anything if the returned status is not recognised
      // This is important for Nuvei 3DS as the front end does not get back a browser message to say the process is complete
      // Instead, we keep polling the order status endpoint whilst the whilst the Invisible Identify frame is rendered
      // To prevent the timeout case triggering after 10s we need to make sure the Nuvei3DSIdentify component is derenderd
      // By setting DeviceDataCollectionComplete, the ThreeDSIdentify component returns null and therefore derenders the Nuvei3DSIdentify component
      if (getShouldCollectThreeDSData(store.getState())) {
        store.dispatch(setDeviceDataCollectionComplete());
      }
      
      stopWithError();
      break;
  }
}
